<template>
  <v-row id="user-management">
    <v-col md="12">
      <div class="users-summary">
        ผู้ใช้งานในบริษัท <strong>{{ users.length }}</strong> คน
        <template v-if="canManageUsers">
          · รอเข้าร่วม <strong :class="pendingInvites.length ? 'tw-text-orange-500' : ''">{{ pendingInvites.length }}</strong> คน
        </template>
      </div>
      <div class="users-search">
        <v-text-field
          v-model="searchKeyword"
          label="ค้นหาผู้ใช้งาน (ชื่อ หรือ อีเมล)"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
        ></v-text-field>
      </div>
      <v-simple-table>
        <template v-slot:default>
          <thead>
          <tr class="header-row">
            <th class="header-text">ชื่อผู้ใช้งาน</th>
            <th class="header-text">อีเมล</th>
            <th class="header-text">บริษัท</th>
            <th class="header-text">สิทธิการใช้งาน</th>
            <th class="header-text tw-w-40"/>
          </tr>
          </thead>
          <tbody>
          <tr v-if="!filteredUsers.length">
            <td colspan="5" class="users-empty">
              {{ searchKeyword && searchKeyword.trim() ? 'ไม่พบผู้ใช้งานที่ค้นหา' : 'ยังไม่มีผู้ใช้งาน' }}
            </td>
          </tr>
          <tr v-for="user in filteredUsers" :key="user.id" :class="userCompany?.user?.email === user?.user?.email  && 'tw-border-l-4 tw-border-super-light-green tw-text-light-green tw-tracking-tight'">
            <td>
              <div class="tw-flex tw-gap-4 tw-items-center">
                <img 
                v-if="user.user.imageProfileUrl"
                :src="user.user.imageProfileUrl + '=s' + Math.round(30 * getDeviceRatio())"
                class="tw-rounded-full tw-w-8 tw-h-8 tw-object-cover"
              /> 
                <img 
                v-else
                src="/images/icons/prefab-pfp.png"
                class="tw-rounded-full tw-w-8 tw-h-8 tw-object-cover"
              /> 
              <span> {{ user.user.nameFirstTH }} {{ user.user.nameLastTH }} </span>
              </div>
            </td>
            <td >{{ user.user.email }}</td>
            <td class="text-center">{{ user.user.companyName || user.company?.nameEN || "-" }}</td>
            <td class="text-center">{{  getDisplayRole(user, selectedCompanyProductList?.isPdfDoc) }}  </td>
            <td v-if="!userAuth?.isEngineer">
              <edit-user v-if="(userCompany.isAdmin && userCompany?.user?.email !== user?.user?.email) && ( !user?.isDeveloper )" :key="`edit-${user.user.id}`" :product="selectedCompanyProductList" :user="user" :userAuth="userAuth"></edit-user>
              <delete-user v-if="userCompany.isAdmin && userCompany?.user?.email !== user?.user?.email && ( !user?.isDeveloper) && ( !user?.user?.isEngineer )" :key="`delete-${user.user.id}`" :userId="user.id"></delete-user>
            </td>
            <td v-else>
              <edit-user v-if="(userCompany.isAdmin && userCompany?.user?.email !== user?.user?.email)" :key="`edit-${user.user.id}`" :product="selectedCompanyProductList" :user="user" :userAuth="userAuth"></edit-user>
              <delete-user v-if="userCompany.isAdmin && userCompany?.user?.email !== user?.user?.email" :key="`delete-${user.user.id}`" :userId="user.id"></delete-user>
            </td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
      <add-user v-if="canManageUsers" :product="selectedCompanyProductList" @invited="loadInvites"></add-user>

      <!-- Invited but not joined yet. RegistersUsers is server-side only, so
           this list comes from apiCompanyInvites rather than a Firestore read. -->
      <section v-if="canManageUsers" class="invites-section">
        <div class="invites-header">
          <h3 class="tw-font-bold tw-text-base tw-mb-0">
            คำเชิญที่รอเข้าร่วม
            <span class="invites-count">{{ pendingInvites.length }}</span>
          </h3>
          <v-btn text small color="#6ec39b" :loading="invitesLoading" @click="loadInvites">
            <v-icon small left>mdi-refresh</v-icon>
            รีเฟรช
          </v-btn>
        </div>

        <p class="invites-hint">
          คนที่ถูกเชิญแล้วแต่ยังไม่ได้กรอกโค้ดเข้าบริษัท — หากอีเมลไม่ถึงปลายทาง
          ให้คัดลอกโค้ดส่งให้โดยตรงได้
        </p>

        <v-alert v-if="invitesError" type="warning" dense text class="tw-text-sm">
          {{ invitesError }}
        </v-alert>

        <v-simple-table>
          <template v-slot:default>
            <thead>
            <tr class="header-row">
              <th class="header-text">อีเมลที่เชิญ</th>
              <th class="header-text">สิทธิการใช้งาน</th>
              <th class="header-text">เชิญโดย</th>
              <th class="header-text">เชิญเมื่อ</th>
              <th class="header-text">สถานะอีเมล</th>
              <th class="header-text tw-w-40"/>
            </tr>
            </thead>
            <tbody>
            <tr v-if="invitesLoading">
              <td colspan="6" class="users-empty">กำลังโหลด...</td>
            </tr>
            <tr v-else-if="!filteredInvites.length">
              <td colspan="6" class="users-empty">
                {{ searchKeyword && searchKeyword.trim() ? 'ไม่พบคำเชิญที่ค้นหา' : 'ไม่มีคำเชิญที่รอเข้าร่วม' }}
              </td>
            </tr>
            <tr v-for="invite in filteredInvites" :key="invite.id">
              <td>{{ invite.email || 'ไม่ทราบอีเมล (คำเชิญเก่า)' }}</td>
              <td class="text-center">{{ inviteRoleLabel(invite.role) }}</td>
              <td class="text-center">{{ invite.invitedBy?.email || '-' }}</td>
              <td class="text-center">{{ formatInviteDate(invite.createdAt) }}</td>
              <td class="text-center">
                <span v-if="invite.emailSent" class="invite-chip invite-chip--ok">ส่งอีเมลแล้ว</span>
                <span v-else-if="invite.emailError" class="invite-chip invite-chip--fail" :title="invite.emailError">ส่งอีเมลไม่สำเร็จ</span>
                <span v-else class="invite-chip">-</span>
              </td>
              <td class="text-center tw-whitespace-nowrap">
                <v-btn icon small :title="copiedInviteId === invite.id ? 'คัดลอกแล้ว' : 'คัดลอกโค้ดลงทะเบียน'"
                       :disabled="!invite.token" @click="copyInviteCode(invite)">
                  <v-icon small :color="copiedInviteId === invite.id ? '#6ec39b' : ''">
                    {{ copiedInviteId === invite.id ? 'mdi-check' : 'mdi-content-copy' }}
                  </v-icon>
                </v-btn>
                <v-btn icon small title="ยกเลิกคำเชิญ" @click="askRevoke(invite)">
                  <v-icon small color="#e8000a">mdi-close-circle-outline</v-icon>
                </v-btn>
              </td>
            </tr>
            </tbody>
          </template>
        </v-simple-table>
      </section>

      <v-dialog v-model="revokeDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="tw-mt-4">
            <div class="tw-font-bold tw-text-xl">ยกเลิกคำเชิญ</div>
          </v-card-title>
          <v-card-text>
            <div class="tw-text-base">
              ยกเลิกคำเชิญของ <strong>{{ revokeTarget?.email || 'รายการนี้' }}</strong> ใช่ไหม
            </div>
            <div class="tw-text-sm tw-text-gray-600 tw-mt-2">
              โค้ดที่ส่งไปแล้วจะใช้งานไม่ได้อีก
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="revoking" indeterminate color="green"></v-progress-circular>
            <v-btn color="black darken-1" text v-if="!revoking" @click="revokeDialog = false" class="tw-tracking-normal tw-text-lg">
              <v-icon left class="tw-text-2xl">mdi-close-circle-outline</v-icon>
              ไม่ใช่
            </v-btn>
            <v-btn color="red darken-1" text v-if="!revoking" @click="confirmRevoke" class="tw-tracking-normal tw-text-lg">
              ใช่
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>

</template>

<script>
import {mapState} from "vuex";
import axios from "axios";
import firebase from "firebase/app";
import "firebase/auth";
import fc from "@/firebaseConfig";
import AddUser from "./add-user";
import DeleteUser from "./delete-user";
import EditUser from "./edit-user";
import getRole from "@/libs/getRole";
import checkSldAdmin from "@/libs/isSldAdmin";

export default {
  name: 'UsersManagement',
  components: {
    AddUser,
    DeleteUser,
    EditUser
  },
  props: {
    userCompany: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      showEmailInput: false,
      searchKeyword: '',
      invites: [],
      invitesLoading: false,
      invitesError: null,
      copiedInviteId: null,
      revokeDialog: false,
      revokeTarget: null,
      revoking: false,
    }
  },
  methods: {
    checkRole: function (user) {
      return getRole(user)
    },

    getDisplayRole(user, isPdfDoc = false) {
      let role = getRole(user)
      switch(role) {
        case 'developer':
          return 'SLD Admin'
        case 'owner':
          return  isPdfDoc ? 'IT Admin' : 'Owner'
        case 'user':
          return 'User'
        default:
          return '-'
      }
    },
    getDeviceRatio() {
      return window.devicePixelRatio
    },

    inviteRoleLabel(role) {
      switch (role) {
        case 'developer':
          return 'SLD Admin'
        case 'owner':
          return this.selectedCompanyProductList?.isPdfDoc ? 'IT Admin' : 'Owner'
        case 'user':
          return 'User'
        default:
          return '-'
      }
    },

    formatInviteDate(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return '-'
      const pad = (n) => String(n).padStart(2, '0')
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },

    async authHeaders() {
      const token = await firebase.auth().currentUser.getIdToken(/* forceRefresh */ false)
      return {
        'Content-Type': 'application/json',
        'Authorization': 'JWT ' + token
      }
    },

    async loadInvites() {
      const companyId = this.$route.params.companyId
      if (!companyId || !this.canManageUsers) return

      this.invitesLoading = true
      this.invitesError = null
      try {
        const headers = await this.authHeaders()
        const res = await axios.get(fc.getFunctionUrl('apiCompanyInvites'), {
          headers,
          params: {companyID: companyId}
        })
        this.invites = (res.data && res.data.invites) || []
      } catch (error) {
        this.invites = []
        // 403 only means this account may not read codes for this company -
        // the section is simply empty, which is not worth an error banner.
        const status = error.response ? error.response.status : null
        this.invitesError = status === 403
          ? null
          : 'ไม่สามารถโหลดรายการคำเชิญได้ กรุณาลองใหม่อีกครั้ง'
      }
      this.invitesLoading = false
    },

    copyInviteCode(invite) {
      if (!invite.token) return
      const done = () => {
        this.copiedInviteId = invite.id
        setTimeout(() => {
          if (this.copiedInviteId === invite.id) this.copiedInviteId = null
        }, 2000)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(invite.token).then(done, () => {})
        return
      }
      // Older browsers on the customer side still land here.
      const el = document.createElement('textarea')
      el.value = invite.token
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      done()
    },

    askRevoke(invite) {
      this.revokeTarget = invite
      this.revokeDialog = true
    },

    async confirmRevoke() {
      const invite = this.revokeTarget
      const companyId = this.$route.params.companyId
      if (!invite || !companyId) return

      this.revoking = true
      try {
        const headers = await this.authHeaders()
        await axios.delete(fc.getFunctionUrl('apiCompanyInvites'), {
          headers,
          params: {companyID: companyId, registerID: invite.id}
        })
        this.invites = this.invites.filter(i => i.id !== invite.id)
        this.revokeDialog = false
        this.revokeTarget = null
      } catch (error) {
        this.invitesError = 'ยกเลิกคำเชิญไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'
        this.revokeDialog = false
      }
      this.revoking = false
    },
  },
  computed: {
    ...mapState({
      users: state => state.user.list,
      userAuth: state => state.user.userData,
      selectedCompanyProductList: (state) =>
        state.companies.selectedCompanyProductList,
    }),
    // Company admins manage their own members; SLD staff manage every
    // customer, including companies they are not a member of. Mirrors the
    // server gate in libs/libSldAdmin.js.
    canManageUsers() {
      return !!(this.userCompany && this.userCompany.isAdmin) ||
        checkSldAdmin(this.userAuth, this.userCompany)
    },

    // Codes that were issued but never redeemed - "invited, not joined yet".
    pendingInvites() {
      return this.invites.filter(invite => !invite.isUsed)
    },

    filteredInvites() {
      const keyword = (this.searchKeyword || '').trim().toLowerCase()
      if (!keyword) return this.pendingInvites
      return this.pendingInvites.filter(invite => {
        const email = (invite.email || '').toLowerCase()
        const inviter = ((invite.invitedBy && invite.invitedBy.email) || '').toLowerCase()
        return email.includes(keyword) || inviter.includes(keyword)
      })
    },

    // Case-insensitive keyword match against the user's Thai/English
    // first+last name or email. Empty keyword returns the full list.
    filteredUsers() {
      const keyword = (this.searchKeyword || '').trim().toLowerCase()
      if (!keyword) return this.users
      return this.users.filter(u => {
        const nameTH = `${u.user?.nameFirstTH || ''} ${u.user?.nameLastTH || ''}`.toLowerCase()
        const nameEN = `${u.user?.nameFirstEN || ''} ${u.user?.nameLastEN || ''}`.toLowerCase()
        const email = (u.user?.email || '').toLowerCase()
        return nameTH.includes(keyword) ||
               nameEN.includes(keyword) ||
               email.includes(keyword)
      })
    },
  },
  beforeDestroy() {
    this.$store.dispatch('user/getUserListUnsubscribe')
  },
  watch: {
    // getRoleUser resolves after created(), and canManageUsers depends on it -
    // load the invites once permission is actually known.
    canManageUsers: {
      immediate: true,
      handler(allowed) {
        if (allowed) this.loadInvites()
      }
    }
  },
  created() {
    let companyId = this.$route.params.companyId
    if (companyId) {
      this.$store.dispatch('user/getList', {companyId: companyId})
      this.$store.dispatch('user/getRoleUser',{companyId: companyId})
    }
  }
}
</script>
<style lang="scss">
#user-management {
  .users-summary {
    color: #616161;
    margin-bottom: 12px;
  }

  .users-search {
    max-width: 480px;
    margin-bottom: 20px;
  }

  .invites-section {
    margin-top: 36px;
  }

  .invites-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .invites-count {
    display: inline-block;
    min-width: 24px;
    padding: 0 7px;
    margin-left: 6px;
    border-radius: 12px;
    background-color: #f0f0f0;
    font-size: 13px;
    text-align: center;
  }

  .invites-hint {
    color: #9e9e9e;
    font-size: 13px;
    margin-bottom: 12px;
  }

  .invite-chip {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    background-color: #f0f0f0;
    color: #616161;
  }

  .invite-chip--ok {
    background-color: #e8f5e9;
    color: #2e7d32;
  }

  .invite-chip--fail {
    background-color: #fdecea;
    color: #c62828;
    cursor: help;
  }

  .users-empty {
    text-align: center !important;
    color: #9e9e9e;
    padding: 24px 0 !important;
  }

  .header-row {
    background-color: #f0f0f0 !important;
  }

  .v-data-table__wrapper {
    border-radius: 14px;
  }

  .header-text {
    font-size: 16px !important;
    font-weight: bold !important;
    text-align: center !important;
  }
}
</style>
